import { z } from "zod";
import { parse, isValid } from "date-fns";
import { ThemedInputType, HandleBlurArgs } from "./props";

/**
 * Maps user date format setting to corresponding date-fns parsing formats
 * Includes flexible variations to handle different input patterns
 */
const mapDateFormatToParseFormats = (userFormat?: string): string[] => {
  switch (userFormat) {
    case "MM/dd/yyyy":
      return ["MM/dd/yyyy", "M/dd/yyyy", "M/d/yyyy", "MM/d/yyyy"];
    case "dd/MM/yyyy":
      return ["dd/MM/yyyy", "d/MM/yyyy", "d/M/yyyy", "dd/M/yyyy"];
    case "yyyy-MM-dd":
      return ["yyyy-MM-dd", "yyyy-M-dd", "yyyy-M-d", "yyyy-MM-d"];
    case "MMM d, yyyy":
      return ["MMM d, yyyy", "MMM dd, yyyy", "MMM d yyyy", "MMM dd yyyy"];
    case "d MMM yyyy":
      return ["d MMM yyyy", "dd MMM yyyy", "d MMM, yyyy", "dd MMM, yyyy"];
    default:
      // Fallback to US format if settings undefined or unrecognized
      return ["MM/dd/yyyy", "M/dd/yyyy", "M/d/yyyy", "MM/d/yyyy"];
  }
};

/**
 * Default validation functions for different input types
 */
export const defaultValidators: Partial<Record<ThemedInputType, (text: string) => boolean>> = {
  date: (s) => z.date().safeParse(s).success,
  "date-time": (s) => z.date().safeParse(s).success,
};

/**
 * Handles input blur events and performs validation
 */
export function handleBlur<T = unknown>({
  type,
  value,
  setIsValid,
  onChangeText,
  required,
  settings,
}: HandleBlurArgs<T>): void {
  switch (type) {
    case "integer":
      try {
        if (!value && !required) return;
        if (!value) return;

        const cleaned = value.replace(/[^\d-]/g, "");
        const numValue = parseInt(cleaned);
        const processedValue = isNaN(numValue) ? "" : numValue.toString();
        onChangeText?.(processedValue);
        setIsValid(!isNaN(numValue));
      } catch {
        setIsValid?.(false);
      }
      break;
    case "float":
      try {
        if (!value && !required) return;
        if (!value) return;

        const numValue = parseFloat(value);
        const processedValue = isNaN(numValue) ? "" : numValue.toString();
        onChangeText?.(processedValue);
        setIsValid(!isNaN(numValue));
      } catch {
        setIsValid?.(false);
      }
      break;
    case "currency":
      try {
        if (!value && !required) return;
        if (!value) return;

        // Parse currency input lazily like "1234" -> "12.34"
        const cleaned = value.replace(/[^\d.-]/g, "");
        let parsed: string;

        if (!cleaned.includes(".") && cleaned.length > 2) {
          const whole = cleaned.slice(0, -2) || "0";
          const decimal = cleaned.slice(-2);
          parsed = `${whole}.${decimal}`;
        } else {
          parsed = cleaned;
        }

        const numValue = parseFloat(parsed);
        const processedValue = isNaN(numValue) ? "" : numValue.toString();
        onChangeText?.(processedValue);
        setIsValid(!isNaN(numValue));
      } catch {
        setIsValid?.(false);
      }
      break;
    case "date":
      try {
        if (!value && !required) return;
        if (!value) return;

        // Get user's preferred date format from settings, with fallback
        const dateFormats = mapDateFormatToParseFormats(settings?.dateFormat);
        let parsed: Date | null = null;

        // Try parsing with each format until one succeeds
        for (const format of dateFormats) {
          const attempt = parse(value, format, new Date());
          if (isValid(attempt)) {
            parsed = attempt;
            break;
          }
        }

        onChangeText?.(value);
        setIsValid(parsed !== null && isValid(parsed));
      } catch {
        setIsValid?.(false);
      }
      break;
    case "date-time":
      try {
        if (!value && !required) return;
        if (!value) return;

        // Get user's preferred date format from settings, with fallback
        const dateFormats = mapDateFormatToParseFormats(settings?.dateFormat);

        // Generate time formats based on user's time format preference
        const timeFormats =
          settings?.timeFormat === "24" ? ["HH:mm", "H:mm"] : ["h:mm a", "hh:mm a", "h:mm A", "hh:mm A"];

        // Create comprehensive date-time format combinations
        const dateTimeFormats: string[] = [];
        for (const dateFormat of dateFormats) {
          for (const timeFormat of timeFormats) {
            dateTimeFormats.push(`${dateFormat} ${timeFormat}`);
          }
        }

        let parsed: Date | null = null;

        // Try parsing with each format until one succeeds
        for (const format of dateTimeFormats) {
          const attempt = parse(value, format, new Date());
          if (isValid(attempt)) {
            parsed = attempt;
            break;
          }
        }

        onChangeText?.(value);
        setIsValid(parsed !== null && isValid(parsed));
      } catch {
        setIsValid?.(false);
      }
      break;
    default:
      if (value !== "" && required) setIsValid?.(false);
      break;
  }
}
